import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiCreditCard,
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiEye,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiClock
} from 'react-icons/fi';

const Payments = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    reservation: '',
    client: '',
    amount: '',
    paymentMethod: 'Credit Card',
    paymentType: 'Rental Fee',
    status: 'Pending',
    transactionId: '',
    cardLast4: '',
    cardBrand: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, reservationsRes, clientsRes] = await Promise.all([
        api.get('/api/payments'),
        api.get('/api/reservations'),
        api.get('/api/clients')
      ]);

      setPayments(paymentsRes.data.data);
      setReservations(reservationsRes.data.data);
      setClients(clientsRes.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Échec de la récupération des données');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentData = {
      ...formData,
      amount: parseFloat(formData.amount),
      cardLast4: formData.cardLast4 || undefined,
      cardBrand: formData.cardBrand || undefined,
      transactionId: formData.transactionId || undefined
    };

    try {
      await api.post('/api/payments', paymentData);
      toast.success('Paiement créé avec succès');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating payment:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de créer des paiements.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Échec de la création du paiement. Veuillez vérifier vos données.');
      }
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      reservation: payment.reservation?._id || payment.reservation || '',
      client: payment.client?._id || payment.client || '',
      amount: payment.amount.toString(),
      paymentMethod: payment.paymentMethod || 'Credit Card',
      paymentType: payment.paymentType || 'Rental Fee',
      status: payment.status || 'Pending',
      transactionId: payment.transactionId || '',
      cardLast4: payment.cardLast4 || '',
      cardBrand: payment.cardBrand || '',
      notes: payment.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const paymentData = {
      ...formData,
      amount: parseFloat(formData.amount),
      cardLast4: formData.cardLast4 || undefined,
      cardBrand: formData.cardBrand || undefined,
      transactionId: formData.transactionId || undefined
    };

    try {
      await api.put(`/api/payments/${editingPayment._id}`, paymentData);
      toast.success('Paiement mis à jour avec succès');
      setShowEditModal(false);
      setEditingPayment(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating payment:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de mettre à jour des paiements.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Échec de la mise à jour du paiement. Veuillez vérifier vos données.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce paiement?')) {
      try {
        await api.delete(`/api/payments/${id}`);
        toast.success('Paiement supprimé avec succès');
        fetchData();
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error('Échec de la suppression du paiement');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      reservation: '',
      client: '',
      amount: '',
      paymentMethod: 'Credit Card',
      paymentType: 'Rental Fee',
      status: 'Pending',
      transactionId: '',
      cardLast4: '',
      cardBrand: '',
      notes: ''
    });
  };

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.transactionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.receiptNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
    const matchesType = paymentTypeFilter === 'all' || payment.paymentType === paymentTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800';
      case 'Failed': return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800';
      case 'Refunded': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300 border border-gray-200 dark:border-gray-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300';
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'Rental Fee': return 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-100';
      case 'Deposit': return 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-100';
      case 'Late Fee': return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 border border-orange-100';
      case 'Damage Fee': return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-100';
      case 'Fuel Fee': return 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-100';
      default: return 'bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'Credit Card':
      case 'Debit Card':
        return <FiCreditCard className="h-4 w-4" />;
      case 'Cash':
        return <FiDollarSign className="h-4 w-4" />;
      case 'Online Payment':
        return <FiDownload className="h-4 w-4" />;
      case 'Bank Transfer':
        return <FiRefreshCw className="h-4 w-4" />;
      case 'Check':
        return <FiCreditCard className="h-4 w-4" />;
      default:
        return <FiCreditCard className="h-4 w-4" />;
    }
  };

  const calculateTotalRevenue = () => {
    return payments
      .filter(p => p.status === 'Completed')
      .reduce((sum, p) => sum + p.amount, 0);
  };

  const calculatePendingAmount = () => {
    return payments
      .filter(p => p.status === 'Pending')
      .reduce((sum, p) => sum + p.amount, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Paiements</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gérer les paiements, factures et transactions financières</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'staff') && (
          <button
            onClick={() => setShowAddModal(true)}
            className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all px-4 py-2 rounded-lg text-sm w-fit"
            title="Ajouter un nouveau paiement"
          >
            <FiPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Nouveau Paiement</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-brand-green hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/40 rounded-full">
              <FiDollarSign className="h-5 w-5 sm:h-8 sm:w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Revenus</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {calculateTotalRevenue().toLocaleString()} MAD
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-yellow-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded-full">
              <FiClock className="h-5 w-5 sm:h-8 sm:w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">En Attente</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {calculatePendingAmount().toLocaleString()} MAD
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-primary-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-primary-100 dark:bg-primary-900/40 rounded-full">
              <FiCreditCard className="h-5 w-5 sm:h-8 sm:w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {payments.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher des paiements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">Tous les Statuts</option>
              <option value="Completed">Terminé</option>
              <option value="Pending">En Attente</option>
              <option value="Failed">Échoué</option>
              <option value="Refunded">Remboursé</option>
            </select>
            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className="input"
            >
              <option value="all">Tous les Types</option>
              <option value="Rental Fee">Frais de Location</option>
              <option value="Deposit">Dépôt</option>
              <option value="Late Fee">Frais de Retard</option>
              <option value="Damage Fee">Frais de Dommage</option>
              <option value="Fuel Fee">Frais de Carburant</option>
              <option value="Other">Autre</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Détails du Paiement
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Montant
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPayments.map((payment) => (
                <tr key={payment._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                          {getPaymentMethodIcon(payment.paymentMethod)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {payment.paymentMethod}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {payment.paymentType}
                        </div>
                        {payment.transactionId && (
                          <div className="text-xs text-gray-400">
                            ID: {payment.transactionId}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {payment.client?.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {payment.client?.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-brand-green transition-colors">
                      ${payment.amount.toFixed(2)} MAD
                    </div>
                    {payment.refundAmount > 0 && (
                      <div className="text-xs text-red-600">
                        Remboursement : {payment.refundAmount.toFixed(2)} MAD
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                    <div className="mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentTypeColor(payment.paymentType)}`}>
                        {payment.paymentType}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(payment.processedAt).toLocaleDateString()}
                    <div className="text-xs">
                      {new Date(payment.processedAt).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/payments/${payment._id}`}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        title="Voir le paiement"
                      >
                        <FiEye className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleEdit(payment)}
                        className="p-2 text-gray-500 hover:text-brand-green hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                        title="Modifier le paiement"
                      >
                        <FiEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(payment._id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Supprimer le paiement"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPayments.length === 0 && (
        <div className="text-center py-12">
          <FiCreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Aucun paiement trouvé</p>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Créer un Nouveau Paiement</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Réservation *
                  </label>
                  <select
                    required
                    value={formData.reservation}
                    onChange={(e) => setFormData({ ...formData, reservation: e.target.value })}
                    className="input"
                  >
                    <option value="">Sélectionner une réservation</option>
                    {reservations.map((reservation) => (
                      <option key={reservation._id} value={reservation._id}>
                        {reservation.car?.brand} {reservation.car?.model} - {reservation.client?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client *
                  </label>
                  <select
                    required
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    className="input"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.name} - {client.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Montant *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type de Paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentType}
                    onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                    className="input"
                  >
                    <option value="Rental Fee">Frais de Location</option>
                    <option value="Deposit">Dépôt</option>
                    <option value="Late Fee">Frais de Retard</option>
                    <option value="Damage Fee">Frais de Dommage</option>
                    <option value="Fuel Fee">Frais de Carburant</option>
                    <option value="Other">Autre</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Méthode de Paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="input"
                  >
                    <option value="Credit Card">Carte de Crédit</option>
                    <option value="Debit Card">Carte de Débit</option>
                    <option value="Cash">Espèces</option>
                    <option value="Online Payment">Paiement en Ligne</option>
                    <option value="Bank Transfer">Virement Bancaire</option>
                    <option value="Check">Chèque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input"
                  >
                    <option value="Pending">En Attente</option>
                    <option value="Completed">Terminé</option>
                    <option value="Failed">Échoué</option>
                    <option value="Refunded">Remboursé</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ID de Transaction
                  </label>
                  <input
                    type="text"
                    value={formData.transactionId}
                    onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                    className="input"
                    placeholder="ID de transaction optionnel"
                  />
                </div>

              </div>

              {(formData.paymentMethod === 'Credit Card' || formData.paymentMethod === 'Debit Card') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marque de la Carte
                    </label>
                    <select
                      value={formData.cardBrand}
                      onChange={(e) => setFormData({ ...formData, cardBrand: e.target.value })}
                      className="input"
                    >
                      <option value="">Sélectionner la marque</option>
                      <option value="Visa">Visa</option>
                      <option value="Mastercard">Mastercard</option>
                      <option value="American Express">American Express</option>
                      <option value="Discover">Discover</option>
                      <option value="Other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Derniers 4 Chiffres
                    </label>
                    <input
                      type="text"
                      maxLength="4"
                      value={formData.cardLast4}
                      onChange={(e) => setFormData({ ...formData, cardLast4: e.target.value })}
                      className="input"
                      placeholder="1234"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  placeholder="Toute note supplémentaire sur le paiement..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Créer le Paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {showEditModal && editingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Modifier le Paiement</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Réservation *
                  </label>
                  <select
                    required
                    value={formData.reservation}
                    onChange={(e) => setFormData({ ...formData, reservation: e.target.value })}
                    className="input"
                  >
                    <option value="">Sélectionner une réservation</option>
                    {reservations.map((reservation) => (
                      <option key={reservation._id} value={reservation._id}>
                        {reservation.car?.brand} {reservation.car?.model} - {reservation.client?.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client *
                  </label>
                  <select
                    required
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    className="input"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.name} - {client.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Montant *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type de Paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentType}
                    onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                    className="input"
                  >
                    <option value="Rental Fee">Frais de Location</option>
                    <option value="Deposit">Dépôt</option>
                    <option value="Late Fee">Frais de Retard</option>
                    <option value="Damage Fee">Frais de Dommage</option>
                    <option value="Fuel Fee">Frais de Carburant</option>
                    <option value="Other">Autre</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Méthode de Paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="input"
                  >
                    <option value="Credit Card">Carte de Crédit</option>
                    <option value="Debit Card">Carte de Débit</option>
                    <option value="Cash">Espèces</option>
                    <option value="Online Payment">Paiement en Ligne</option>
                    <option value="Bank Transfer">Virement Bancaire</option>
                    <option value="Check">Chèque</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input"
                  >
                    <option value="Pending">En Attente</option>
                    <option value="Completed">Terminé</option>
                    <option value="Failed">Échoué</option>
                    <option value="Refunded">Remboursé</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    ID de Transaction
                  </label>
                  <input
                    type="text"
                    value={formData.transactionId}
                    onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                    className="input"
                    placeholder="ID de transaction optionnel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    placeholder="Toute note supplémentaire sur le paiement..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="input"
                    rows="3"
                  />
                </div>
              </div>

              {(formData.paymentMethod === 'Credit Card' || formData.paymentMethod === 'Debit Card') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Marque de la Carte
                    </label>
                    <select
                      value={formData.cardBrand}
                      onChange={(e) => setFormData({ ...formData, cardBrand: e.target.value })}
                      className="input"
                    >
                      <option value="">Sélectionner la marque</option>
                      <option value="Visa">Visa</option>
                      <option value="Mastercard">Mastercard</option>
                      <option value="American Express">American Express</option>
                      <option value="Discover">Discover</option>
                      <option value="Other">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Derniers 4 Chiffres
                    </label>
                    <input
                      type="text"
                      maxLength="4"
                      value={formData.cardLast4}
                      onChange={(e) => setFormData({ ...formData, cardLast4: e.target.value })}
                      className="input"
                      placeholder="1234"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  placeholder="Toute note supplémentaire sur le paiement..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPayment(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Mettre à Jour le Paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments; 